"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import OceanImg from "@/assets/images/hero/Ocean.webp";
import MiddlegroundLeftImg from "@/assets/images/hero/MiddlegroundLeft.webp";
import MiddlegroundRightImg from "@/assets/images/hero/MiddlegroundRight.webp";
import HotAirBalloonImg from "@/assets/images/hero/HotAirBalloon.webp";
import ForegroundLeftImg from "@/assets/images/hero/ForegroundLeft.webp";
import ForegroundRightImg from "@/assets/images/hero/ForegroundRight.webp";

export default function HeroParallaxMotion() {
  const { scrollY } = useScroll();

  // Transform values for different parallax layers
  const oceanScale = useTransform(scrollY, [0, 400], [1, 0.8]);
  const foregroundY = useTransform(scrollY, [0, 400], [0, -30]);
  const middlegroundY = useTransform(scrollY, [0, 400], [0, 45]);
  const middlegroundScale = useTransform(scrollY, [0, 400], [1, 0.9]);
  const balloonY = useTransform(scrollY, [0, 400], [0, 45]);

  // Refs and state for managing animations on navigation
  const middleLeftRef = useRef(null);
  const middleRightRef = useRef(null);
  const foregroundLeftRef = useRef(null);
  const foregroundRightRef = useRef(null);

  const middleLeftInView = useInView(middleLeftRef, { once: false, margin: "-20%" });
  const middleRightInView = useInView(middleRightRef, { once: false, margin: "-20%" });
  const foregroundLeftInView = useInView(foregroundLeftRef, { once: false, margin: "-30%" });
  const foregroundRightInView = useInView(foregroundRightRef, { once: false, margin: "-30%" });

  const [middleLeftAnimated, setMiddleLeftAnimated] = useState(false);
  const [middleRightAnimated, setMiddleRightAnimated] = useState(false);
  const [foregroundLeftAnimated, setForegroundLeftAnimated] = useState(false);
  const [foregroundRightAnimated, setForegroundRightAnimated] = useState(false);

  // Reset animation states when component mounts (on navigation)
  useEffect(() => {
    const resetTimer = setTimeout(() => {
      setMiddleLeftAnimated(false);
      setMiddleRightAnimated(false);
      setForegroundLeftAnimated(false);
      setForegroundRightAnimated(false);
    }, 100);

    // Start background animations with staggered delays
    const startAnimations = setTimeout(() => {
      setMiddleLeftAnimated(true);
      setTimeout(() => setMiddleRightAnimated(true), 100);
      setTimeout(() => setForegroundLeftAnimated(true), 200);
      setTimeout(() => setForegroundRightAnimated(true), 300);
    }, 600); // Start after main content has had time to animate

    return () => {
      clearTimeout(resetTimer);
      clearTimeout(startAnimations);
    };
  }, []);

  // Track animation states
  useEffect(() => {
    if (middleLeftInView && !middleLeftAnimated) setMiddleLeftAnimated(true);
  }, [middleLeftInView, middleLeftAnimated]);

  useEffect(() => {
    if (middleRightInView && !middleRightAnimated) setMiddleRightAnimated(true);
  }, [middleRightInView, middleRightAnimated]);

  useEffect(() => {
    if (foregroundLeftInView && !foregroundLeftAnimated) setForegroundLeftAnimated(true);
  }, [foregroundLeftInView, foregroundLeftAnimated]);

  useEffect(() => {
    if (foregroundRightInView && !foregroundRightAnimated) setForegroundRightAnimated(true);
  }, [foregroundRightInView, foregroundRightAnimated]);

  return (
    <>
      <motion.img
        style={{
          scaleY: oceanScale,
          maskImage: `linear-gradient(to bottom, transparent, black 19%)`
        }}
        src={OceanImg.src}
        alt="ocean"
        className="absolute bottom-0 w-full h-[36rem] lg:h-[41rem] brightness-105 z-0"
      />

      <motion.img
        ref={middleLeftRef}
        style={{ scaleY: middlegroundScale, translateY: middlegroundY }}
        initial={{ opacity: 0, x: -40 }}
        animate={middleLeftAnimated ? { x: 0, opacity: 1 } : { opacity: 0, x: -40 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        src={MiddlegroundLeftImg.src}
        alt="Middleground island left side"
        className="absolute -left-10 md:left-0 top-[38%] lg:top-[30%] h-[10%] aspect-auto z-10 blur-[0.5px]"
      />

      <motion.img
        ref={middleRightRef}
        style={{ scaleY: middlegroundScale, translateY: middlegroundY }}
        initial={{ opacity: 0, x: 40 }}
        animate={middleRightAnimated ? { x: 0, opacity: 1 } : { opacity: 0, x: 40 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        src={MiddlegroundRightImg.src}
        alt="Middleground island right side"
        className="absolute -right-2 sm:-right-5 md:right-0 top-[38%] lg:top-[30%] h-[10%] aspect-auto z-10 blur-[0.5px]"
      />

      <motion.img
        style={{ y: balloonY }}
        initial={{ y: 80 }}
        animate={{ y: 0 }}
        transition={{
          duration: 1.5,
          ease: "easeInOut",
          type: "spring",
          stiffness: 50
        }}
        src={HotAirBalloonImg.src}
        alt="hot air balloon"
        className="absolute w-[100px] h-[120px] -top-10 lg:top-24 -left-10 lg:left-10 2xl:left-[5rem] z-30"
      />

      <motion.img
        style={{ y: balloonY }}
        initial={{ y: 80 }}
        animate={{ y: 0 }}
        transition={{
          duration: 1.5,
          ease: "easeInOut",
          type: "spring",
          stiffness: 50
        }}
        src={HotAirBalloonImg.src}
        alt="hot air balloon"
        className="hidden sm:block sm:absolute w-[75px] h-[85px] blur-sm top-40 right-10 z-30"
      />

      <div className="absolute bottom-[85px] w-full h-[70px] bg-gradient-to-t from-[#FFFFF0] to-transparent z-25" />
      <div className="absolute bottom-0 w-full h-[85px] bg-[#FFFFF0] z-25" />

      <motion.img
        ref={foregroundLeftRef}
        style={{ translateY: foregroundY }}
        initial={{ opacity: 0, x: -40 }}
        animate={foregroundLeftAnimated ? { x: 0, opacity: 1 } : { opacity: 0, x: -40 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        src={ForegroundLeftImg.src}
        alt="Foreground island left side"
        className="absolute -left-5 lg:left-0 bottom-10 lg:-bottom-20 h-[50%] lg:h-[70%] max-w-[50%] aspect-auto z-20"
      />

      <motion.img
        ref={foregroundRightRef}
        style={{ translateY: foregroundY }}
        initial={{ opacity: 0, x: 40 }}
        animate={foregroundRightAnimated ? { x: 0, opacity: 1 } : { opacity: 0, x: 40 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        src={ForegroundRightImg.src}
        alt="Foreground island right side"
        className="absolute -right-20 sm:-right-10 bottom-10 lg:bottom-[0] h-[45%] lg:h-[60%] max-w-[50%] aspect-auto z-20"
      />
    </>
  );
}
